'use strict';

var encode = require('hashcode').hashCode;
var _ = require('lodash');
var JQXTreeFactory = function JQXTreeFactory(JQXTreeAdapterService, GroupNode, ColumnNode, CustomColumnNode, SplitterNode, GridColumn, GridColumnGroup, TreeNodeIdGenerator, columnIterator, CycleDetectionService) {
  var EMPTY_NODE_LABEL = '&nbsp;';
  function Tree(scope) {
    this.originalApplicationDefaultSettings = _.cloneDeep(scope.applicationDefaultSettings);
    this.applicationDefaultSettings = null;
    this.applicationDefaultSourceHashCode = null;
    this.ddData = null;
    this.freezeColumnDuringConversion = true;
    this.source = null;
    this.settings = scope.settings;
    this.dataFields = this.prepareDataFields(scope.fields);
    this.treeSettings = this.getTreeSettings(scope.settings, _.cloneDeep(this.originalApplicationDefaultSettings));
    scope.treeSettings = this.treeSettings;
  }
  function deepFind(collection, subCollectionKey, id) {
    var result = null;
    _.forEach(collection, function (item) {
      if (item.id === id) {
        result = item;
      } else {
        _.forEach(item[subCollectionKey], function (subItem) {
          if (subItem.id === id) {
            result = subItem;
            return false;
          }
        });
      }
      return !result;
    });
    return result;
  }
  function deepFindByField(collection, subCollectionKey, field) {
    var result = null;
    _.forEach(collection, function (item) {
      if (item.field === field) {
        result = item;
      } else {
        _.forEach(item[subCollectionKey], function (subItem) {
          if (subItem.field === field) {
            result = subItem;
            return false;
          }
        });
      }
      return !result;
    });
    return result;
  }
  Tree.prototype.addNewItem = function (item, previousItemId) {
    if (_.isString(previousItemId) === true) {
      var location = this.findIndexAndCollection(previousItemId);
      if (location !== null) {
        location.collection.splice(location.index, 0, item);
        return;
      }
    }
    this.source.push(item);
  };
  Tree.prototype.addCalculatedColumn = function (label, previousItemId) {
    var id = this.createNodeId(label);
    var column = new CustomColumnNode({
      id: id,
      field: id,
      gridId: this.settings.gridId,
      label: label,
      checked: true,
      selected: false,
      frozen: false,
      formula: {
        operator: '+'
      }
    });
    this.addNewItem(column, previousItemId);
    this.reapplySourceAndSelect(id);
    return id;
  };
  Tree.prototype.addGroup = function (label, previousItemId) {
    var id = this.createNodeId(label);
    var group = new GroupNode({
      id: id,
      label: label,
      checked: true,
      expanded: true,
      items: [],
      selected: false,
      frozen: false,
      custom: true
    });
    this.addNewItem(group, previousItemId);
    this.reapplySourceAndSelect(id);
    return id;
  };
  Tree.prototype.buildColumnsHash = function (columns) {
    var values = [];
    this.forEachColumn(columns, function (col) {
      values.push(col.id + '-' + Number(col.checked) + '-' + Number(col.frozen));
    });
    return encode().value(values.join('-'));
  };
  Tree.prototype.checkItem = function (itemId, checked) {
    var item = this.getNodeById(itemId);
    if (item !== null) {
      item.checked = checked;
    }
  };
  Tree.prototype.createColumnFromNode = function (node) {
    var data;
    var column = null;
    data = Object.create(null);
    data.custom = node.custom;
    data.field = node.field;
    data.title = node.label;
    data.frozen = node.frozen;
    data.visible = node.checked;
    data.formula = node.formula;
    data.gridId = this.settings.gridId;
    if (data.formula) {
      data.renderer = 'calculatedValue';
    }
    if (this.isColumnNode(node) || this.isCustomColumnNode(node)) {
      column = new GridColumn(data);
    } else if (this.isGroupNode(node)) {
      var gridIdRegexp = new RegExp(this.settings.gridId + '\\-');
      if (!gridIdRegexp.test(node.id)) {
        data.groupId = node.id;
      } else {
        data.groupId = node.id.replace(this.settings.gridId + '-', '');
      }
      data.dataDrivenColumn = node.dataDrivenColumn;
      data.columns = _.transform(node.items, this.nodeToColumn.bind(this));
      column = new GridColumnGroup(data);
    }
    return column;
  };

  /**
   * Creates a unique node ID based on node's label.
   *
   * As the ID is time-based, there's a minimal (but still possible!),
   * chance such ID already exists in our tree (computer's clock set
   * wrong, etc.). To avoid collisions, we check for the existence of
   * such ID in the tree and generate new one if required.
   *
   * It's little paranoid, I know. But definitely better than the
   * ID generator provided by jqWidgets, which is a total cr*p.
   *
   * @param {string} label
   * @returns {string}
   */
  Tree.prototype.createNodeId = function (label) {
    var id;
    do {
      id = TreeNodeIdGenerator.getNewNodeId(label);
    } while (this.getNodeById(id) !== null);
    return id;
  };
  Tree.prototype.ensureItemIsGroup = function (item) {
    var node = this.getNodeById(item.id);
    if (this.isGroupNode(node) === false) {
      throw new Error(item.id + ' is not a group');
    }
  };
  Tree.prototype.findIndexAndCollection = function (id) {
    var item;
    var i;
    var j;
    var srcLen;
    var itemsLen;
    var subItem;
    for (i = 0, srcLen = this.source.length; i < srcLen; i++) {
      item = this.source[i];
      if (item.id === id) {
        return {
          index: i,
          collection: this.source
        };
      }
      if (item.items) {
        for (j = 0, itemsLen = item.items.length; j < itemsLen; j++) {
          subItem = item.items[j];
          if (subItem.id === id) {
            return {
              index: j,
              collection: item.items
            };
          }
        }
      }
    }
    return null;
  };
  Tree.prototype.forEachColumn = function (columns, callback, context) {
    return columnIterator.forEachColumn(columns, callback, context || this);
  };
  Tree.prototype.isNumericColumn = function (col) {
    return col.custom === true || this.dataFields[col.field] === 'number';
  };
  function getColumnNameWithGroup(col, group) {
    if (group) {
      return group.label + ' - ' + col.label;
    } else {
      return col.label;
    }
  }
  Tree.prototype.getCalculableColumns = function (excludeCurrent) {
    var columns = [];
    var current;
    if (excludeCurrent) {
      current = this.getSelectedNode();
    }
    this.forEachColumn(this.source, function (col, i, group) {
      if (this.isColumnNode(col) === false || current && current.id === col.id || !this.isNumericColumn(col)) {
        return;
      }
      columns.push({
        id: col.field,
        label: getColumnNameWithGroup(col, group)
      });
    });
    return columns;
  };
  Tree.prototype.getLabelForNode = function (id) {
    var label = null;
    this.forEachColumn(this.source, function (col, i, group) {
      if (col.id === id) {
        label = getColumnNameWithGroup(col, group);
        return false;
      }
    });
    return label;
  };
  Tree.prototype.getClosestFirstLevelSibling = function (item) {
    // for a group element return its parent
    if (item.level > 0) {
      return this.getTreeNodeById(item.parentId);
    }

    // for a first-level element return its predecessor
    var location = this.findIndexAndCollection(item.id);
    if (location !== null && location.index > 0 && location.collection.length > 0) {
      var elem = location.collection[location.index - 1];
      return this.getTreeNodeById(elem.id);
    }
    return null;
  };
  Tree.prototype.getCustomColumns = function () {
    var customColumns = [];
    this.forEachColumn(this.source, function (node) {
      if (this.isCustomColumnNode(node)) {
        customColumns.push(node);
      }
    });
    return customColumns;
  };
  Tree.prototype.getCommonModelFromTreeModel = function () {
    var columns;
    this.freezeColumnDuringConversion = true;
    columns = _.transform(this.source, _.bind(this.nodeToColumn, this), null);
    return columns;
  };
  Tree.prototype.getDependentCustomColumns = function (id, collection) {
    var currentCollection = collection || _.filter(this.getCustomColumns(), function (col) {
      return col.id !== id;
    });
    return _.filter(currentCollection, function (customColumn) {
      return id.match(customColumn.formula.leftOperand) || id.match(customColumn.formula.rightOperand);
    });
  };
  Tree.prototype.getFirstTreeItem = function () {
    var items = this.getTreeItems();
    return items[0] ? items[0] : null;
  };
  Tree.prototype.getGridColumnById = function (id) {
    return deepFind(this.settings.columns, 'columns', id);
  };
  Tree.prototype.getNodeById = function (id) {
    return deepFind(this.source, 'items', id);
  };
  Tree.prototype.getNodeByField = function (field) {
    return deepFindByField(this.source, 'items', field);
  };
  Tree.prototype.getSelectedNodeLabel = function () {
    var selectedNode = this.getSelectedNode();
    return _.unescape(selectedNode.label);
  };
  Tree.prototype.getSource = function (gridSettings) {
    return JQXTreeAdapterService.getDataAdapter(gridSettings);
  };
  Tree.prototype.getTreeNodeById = function (id) {
    return _.find(this.getTreeItems(), {
      id: id
    });
  };
  Tree.prototype.prepareDataFields = function (data) {
    return data.reduce(function (obj, k) {
      obj[k.name] = k.type;
      return obj;
    }, {});
  };
  Tree.prototype.getTreeItems = function () {
    return this.treeSettings.apply('getItems');
  };
  Tree.prototype.getTreeSettings = function (settings, defaultSettings) {
    this.source = this.getSource(settings);
    this.applicationDefaultSettings = defaultSettings;
    this.applicationDefaultSourceHashCode = this.buildColumnsHash(this.getSource(defaultSettings));
    return {
      width: '100%',
      height: '400px',
      checkSize: 24,
      hasThreeStates: true,
      checkboxes: true,
      keyboardNavigation: true,
      ignoreCtrlKeyCombinations: true,
      allowSelectionRemovalOnDisabledTree: true,
      allowDrag: true,
      allowDrop: true,
      source: this.source,
      dragEnd: this.isDroppingAllowedAndSetDragAndDropData.bind(this)
    };
  };
  Tree.prototype.getSelectedItem = function () {
    return this.treeSettings.apply('selectedItem');
  };
  Tree.prototype.getSelectedNode = function () {
    var columns = [];
    this.forEachColumn(this.source, function (col) {
      columns.push(col);
    });
    return _.find(columns, {
      selected: true
    });
  };
  Tree.prototype.hasDependentCustomColumns = function (id, collection) {
    var currentCollection = collection || _.filter(this.getCustomColumns(), function (col) {
      return col.id !== id;
    });
    return !_.isEmpty(this.getDependentCustomColumns(id, currentCollection));
  };
  Tree.prototype.hasItemSibling = function (id, prevSibling) {
    var treeItem = this.findIndexAndCollection(id);
    return treeItem !== null && (prevSibling && treeItem.index > 0 || !prevSibling && treeItem.index < treeItem.collection.length - 1);
  };
  Tree.prototype.insertNodeAfterClosestGroup = function (draggedNode) {
    var node = this.getTreeNodeById(draggedNode.id);
    var parentItem = this.getClosestFirstLevelSibling(node);
    var parentNodeData = this.findIndexAndCollection(parentItem.id);
    parentNodeData.collection.splice(parentNodeData.index + 1, 0, draggedNode);
  };
  Tree.prototype.isAddingRemovingDisallowed = function (selectedItem, itemLevel) {
    var prevItem = this.getClosestFirstLevelSibling(selectedItem);
    if (!prevItem || this.isSplitter(selectedItem.id)) {
      return true;
    }
    var prevNode = this.getNodeById(prevItem.id);
    return !(this.isGroup(prevItem.id) && !this.isGroup(selectedItem.id) && selectedItem.level === itemLevel && !prevNode.dataDrivenColumn);
  };
  Tree.prototype.isAddingToGroupDisabled = function (selectedItem) {
    return this.isAddingRemovingDisallowed(selectedItem, 0);
  };
  Tree.prototype.isApplicationDefaultTree = function () {
    return this.buildColumnsHash(this.source) === this.applicationDefaultSourceHashCode;
  };
  Tree.prototype.isColumnNode = function (node) {
    return node instanceof ColumnNode;
  };
  Tree.prototype.isCustomColumnNode = function (node) {
    return node instanceof CustomColumnNode;
  };
  Tree.prototype.isDroppingAllowedAndSetDragAndDropData = function (dragItem, dropItem, args, dropPosition) {
    if (!dragItem || !dropItem) {
      return false;
    }
    var theSameItem = dragItem.id === dropItem.id;
    var movement = dropPosition === 'before' || dropPosition === 'after';
    var inserting = dropPosition === 'inside';
    var dragNode = this.getNodeById(dragItem.id);
    var dropNode = this.getNodeById(dropItem.id);
    var isDragDataDrivenGroup = !!dragNode.dataDrivenColumn;
    var isDropDataDrivenGroup = !!dropNode.dataDrivenColumn;
    var allowed = !theSameItem && (movement || inserting && this.isGroup(dropItem.id)) && !isDropDataDrivenGroup && !(isDragDataDrivenGroup && inserting);
    if (allowed) {
      this.ddData = Object.create(null);
      this.ddData.dragItem = dragItem;
      this.ddData.dropItem = dropItem;
      this.ddData.dropPosition = dropPosition;
    }
    return allowed;
  };
  Tree.prototype.isEditableColumn = function (id) {
    var node = this.getNodeById(id);
    return this.isCustomColumnNode(node);
  };
  Tree.prototype.isEditableGroup = function (id) {
    var node = this.getNodeById(id);
    return this.isGroupNode(node) && node.custom === true && !node.dataDrivenColumn;
  };
  Tree.prototype.isGroup = function (id) {
    var node = this.getNodeById(id);
    return this.isGroupNode(node);
  };
  Tree.prototype.isGroupNode = function (node) {
    return node instanceof GroupNode;
  };
  Tree.prototype.isRemovingFromGroupDisabled = function (selectedItem) {
    return this.isAddingRemovingDisallowed(selectedItem, 1);
  };
  Tree.prototype.isSplitter = function (id) {
    return id.indexOf('_splitter') === 0;
  };
  Tree.prototype.isSplitterNode = function (node) {
    return node instanceof SplitterNode;
  };
  function getNameToCompare(label) {
    return label ? label.toLowerCase().trim() : '';
  }
  Tree.prototype.isValidNewGroupName = function (newLabel) {
    var newGroupName = getNameToCompare(newLabel);
    var isGroupNode = this.isGroupNode;
    var foundNodes = _.filter(this.source, function (node) {
      return isGroupNode(node) && newGroupName === getNameToCompare(node.label);
    });
    return foundNodes.length === 0;
  };
  Tree.prototype.isValidNewColumnName = function (newLabel) {
    var newGroupName = getNameToCompare(newLabel);
    var isGroupNode = this.isGroupNode;
    var foundColumns = [];
    this.forEachColumn(this.source, function (col) {
      if (!isGroupNode(col) && newGroupName === getNameToCompare(col.label)) {
        foundColumns.push(col);
      }
    });
    return foundColumns.length === 0;
  };
  Tree.prototype.isValidColumnOrValue = function (value) {
    if (value) {
      if (value.id) {
        return this.getNodeByField(value.id) !== null;
      } else {
        var label = value.label;
        if (_.isNumber(label)) {
          return true;
        } else {
          var isNumber = label !== undefined && label !== null && label.trim() !== '' && !isNaN(label);
          var isZero = isNumber && parseFloat(label) === 0;
          return isNumber && !isZero;
        }
      }
    }
    return false;
  };
  Tree.prototype.moveItem = function (item, upSearch) {
    if (this.hasItemSibling(item.id, upSearch) === false) {
      return;
    }
    var treeItem = this.findIndexAndCollection(item.id);
    var realItem = treeItem.collection[treeItem.index];
    treeItem.collection.splice(treeItem.index, 1);
    var newPos = upSearch ? treeItem.index - 1 : treeItem.index + 1;
    treeItem.collection.splice(newPos, 0, realItem);
    this.reapplySourceAndSelect(item.id);
  };
  Tree.prototype.moveItemDown = function (item) {
    this.moveItem(item, false);
  };
  Tree.prototype.moveItemToGroup = function (item) {
    var group = this.getClosestFirstLevelSibling(item);
    this.ensureItemIsGroup(group);
    var columnIndex = _.findIndex(this.source, {
      id: item.id
    });
    var groupIndex = _.findIndex(this.source, {
      id: group.id
    });
    var column = this.source[columnIndex];
    this.source[groupIndex].items.push(this.source[columnIndex]);
    this.source.splice(columnIndex, 1);
    if (column.isChildOf(group)) {
      column.hideGroup();
    }
    this.reapplySourceAndSelect(item.id);
  };
  Tree.prototype.moveItemUp = function (item) {
    this.moveItem(item, true);
  };
  Tree.prototype.nodeToColumn = function (result, node) {
    var column = null;
    if (this.isSplitterNode(node) === true) {
      this.freezeColumnDuringConversion = false;
    }
    if (this.isColumnNode(node) || this.isGroupNode(node) || this.isCustomColumnNode(node)) {
      column = this.getGridColumnById(node.id);
      if (column && (this.isColumnNode(node) || this.isCustomColumnNode(node))) {
        column = this.updateColumnFromNode.call(this, column, node);
      } else {
        column = this.createColumnFromNode.call(this, node);
      }
      column.frozen = this.freezeColumnDuringConversion;
    }
    if (column !== null) {
      return result.push(column);
    }
  };
  Tree.prototype.placingColumnAfterGroup = function (draggedNode, droppedNode, dropPosition) {
    return !this.isGroupNode(draggedNode) && this.isGroupNode(droppedNode) && dropPosition === 'after';
  };
  Tree.prototype.placingNodeInsideGroup = function (ddData) {
    return ddData.dropItem.level > 0 || ddData.dropPosition === 'inside';
  };
  Tree.prototype.ready = function () {
    return _.isFunction(this.treeSettings.apply);
  };
  Tree.prototype.reapplySource = function () {
    this.treeSettings.apply('source', this.source);
  };
  Tree.prototype.reapplySourceAndSelect = function (id) {
    this.selectNothing();
    this.reapplySource();
    this.setSelectedItem(id);
  };
  Tree.prototype.rebuildTree = function (settings) {
    this.treeSettings.apply('clear');
    this.settings = settings;
    this.source = this.getSource(this.settings);
    this.selectNothing();
    this.reapplySource();
  };
  Tree.prototype.traverseAndRemoveCustomColumn = function (currentCollection, customColumnId) {
    this.forEachColumn(currentCollection, function (column, index) {
      if (this.isGroupNode(column)) {
        this.traverseAndRemoveCustomColumn(column.items, customColumnId);
      } else {
        if (column.id === customColumnId) {
          currentCollection.splice(index, 1);
          return false;
        }
      }
    });
  };
  Tree.prototype.removeCustomColumnRecursively = function (collection, customColumnId) {
    var currentCollection = collection || _.filter(this.getCustomColumns(), function (col) {
      return col.id !== customColumnId;
    });
    if (this.hasDependentCustomColumns(customColumnId, currentCollection)) {
      var dependentColumns = this.getDependentCustomColumns(customColumnId, currentCollection);
      dependentColumns.forEach(function (column) {
        this.removeCustomColumnRecursively(_.filter(currentCollection, function (col) {
          return col.id !== column.id;
        }), column.id);
      }, this);
    }
    this.traverseAndRemoveCustomColumn(this.source, customColumnId);
  };
  Tree.prototype.removeCustomColumn = function (customColumnId, collection) {
    this.removeCustomColumnRecursively(collection, customColumnId);
    this.selectNothing();
    this.reapplySource();
  };
  Tree.prototype.removeCustomGroup = function (groupId) {
    this.unGroupAllElements(groupId);
    this.selectNothing();
    this.reapplySource();
  };
  Tree.prototype.removeItemFromGroup = function (item) {
    var group = this.getClosestFirstLevelSibling(item);
    this.ensureItemIsGroup(group);
    var groupIndex = _.findIndex(this.source, {
      id: group.id
    });
    var columnIndex = _.findIndex(this.source[groupIndex].items, {
      id: item.id
    });
    var column = this.source[groupIndex].items[columnIndex];
    this.source.splice(groupIndex + 1, 0, column);
    this.source[groupIndex].items.splice(columnIndex, 1);
    column.showGroup();
    this.reapplySourceAndSelect(item.id);
  };
  Tree.prototype.cycleDetected = function () {
    return CycleDetectionService.getCycles(this.source).length > 0;
  };
  Tree.prototype.resetNodeSelection = function () {
    this.forEachColumn(this.source, function (column) {
      column.selected = false;
    });
  };
  Tree.prototype.resetSettings = function () {
    this.source = this.getSource(_.cloneDeep(this.originalApplicationDefaultSettings));
    this.selectNothing();
    this.reapplySource();
  };
  Tree.prototype.selectNothing = function () {
    this.resetNodeSelection();
    this.treeSettings.apply('selectItem', null);
  };
  Tree.prototype.setItemLabel = function (item, label) {
    var escapedLabel = _.escape(label);
    var itemLabel = label === '' ? EMPTY_NODE_LABEL : escapedLabel;
    var node = this.getNodeById(item.id);
    node.label = escapedLabel;
    this.treeSettings.apply('updateItem', item, {
      label: itemLabel
    });
  };
  Tree.prototype.setSelectedItem = function (id) {
    var treeNode = this.getTreeNodeById(id);
    this.treeSettings.apply('selectItem', treeNode);
    this.treeSettings.apply('ensureVisible', treeNode.element);
  };
  Tree.prototype.setItemVisible = function (id) {
    var treeNode = this.getTreeNodeById(id);
    this.treeSettings.apply('selectItem', treeNode);
    this.treeSettings.apply('enableItem', treeNode.element);
  };
  Tree.prototype.setSelectedNode = function (id) {
    var node = this.getNodeById(id);
    this.resetNodeSelection();
    node.selected = true;
  };
  Tree.prototype.grabFocus = function () {
    this.treeSettings.apply('focus');
  };
  Tree.prototype.unGroupAllElements = function (groupId) {
    var groupIndex = _.findIndex(this.source, {
      id: groupId
    });
    var spliceArgs = this.source[groupIndex].items;
    spliceArgs.unshift(groupIndex + 1, 0);
    this.source.splice.apply(this.source, spliceArgs);
    this.source.splice(groupIndex, 1);
  };
  Tree.prototype.updateColumnFromNode = function (column, node) {
    column.title = node.label;
    column.frozen = node.frozen;
    column.visible = node.checked;
    column.formula = node.formula;
    return column;
  };
  Tree.prototype.updateTitleAfterDragAndDrop = function () {
    var dragData = this.findIndexAndCollection(this.ddData.dragItem.id);
    var draggedNode = dragData.collection[dragData.index];
    if (!(draggedNode instanceof ColumnNode)) {
      return;
    }
    var dropData = this.findIndexAndCollection(this.ddData.dropItem.id);
    var droppedNode = dropData.collection[dropData.index];
    var dropPosition = this.ddData.dropPosition;
    var dropLevel = this.ddData.dropItem.level;
    if (dropLevel < 1 && !this.placingColumnAfterGroup(draggedNode, droppedNode, dropPosition) && !this.placingNodeInsideGroup(this.ddData)) {
      _.forEach(this.source, function (node) {
        if (node instanceof GroupNode && node.id === draggedNode.groupId && !node.custom) {
          draggedNode.showGroup();
        }
      });
    } else {
      if (draggedNode.isChildOf(this.ddData.dropItem.parentId || droppedNode)) {
        draggedNode.hideGroup();
      } else {
        _.forEach(this.source, function (node) {
          if (node instanceof GroupNode && node.id === draggedNode.groupId && !node.custom) {
            draggedNode.showGroup();
          }
        });
      }
    }
    this.treeSettings.apply('updateItem', draggedNode, {
      label: draggedNode.label
    });
  };
  Tree.prototype.updateTreeModelAfterDragAndDrop = function () {
    if (this.ddData === null) {
      return;
    }
    var reapplyChanges = false;
    var dropPosition = this.ddData.dropPosition;
    var draggedItemId = this.ddData.dragItem.id;
    var dragData = this.findIndexAndCollection(draggedItemId);
    var draggedNode = dragData.collection[dragData.index];
    dragData.collection.splice(dragData.index, 1);
    var dropData = this.findIndexAndCollection(this.ddData.dropItem.id);
    var droppedNode = dropData.collection[dropData.index];
    var isSplitterOrGroup = this.isSplitter(draggedItemId) || this.isGroupNode(draggedNode);

    /* special case: we drop a column just below a group name: add to the group instead of placing after it */
    if (!isSplitterOrGroup && this.placingColumnAfterGroup(draggedNode, droppedNode, dropPosition)) {
      droppedNode.items.unshift(draggedNode);
      reapplyChanges = true;

      /* special case: we drop splitter or a group inside another group: push the dragged element after the group */
    } else if (isSplitterOrGroup && this.placingNodeInsideGroup(this.ddData)) {
      this.insertNodeAfterClosestGroup(draggedNode);
      reapplyChanges = true;
    } else if (dropPosition === 'after') {
      dropData.collection.splice(dropData.index + 1, 0, draggedNode);
    } else if (dropPosition === 'before') {
      dropData.collection.splice(dropData.index, 0, draggedNode);
    } else if (dropPosition === 'inside') {
      droppedNode.items.push(draggedNode);
    }
    this.updateTitleAfterDragAndDrop();
    if (reapplyChanges) {
      this.reapplySourceAndSelect(draggedItemId);
    } else {
      this.setItemVisible(draggedItemId);
    }
    this.ddData = null;
  };
  Tree.prototype.updateFormula = function (leftOperand, operator, rightOperand) {
    var node = this.getSelectedNode();
    var formula = node.formula;
    formula.leftOperand = leftOperand;
    formula.operator = operator;
    formula.rightOperand = rightOperand;
  };
  return Tree.bind(this);
};
JQXTreeFactory.$inject = ['JQXTreeAdapterService', 'JQXTreeNodeGroupFactory', 'JQXTreeNodeColumnFactory', 'JQXTreeNodeCustomColumnFactory', 'JQXTreeNodeSplitterFactory', 'GridColumnFactory', 'GridColumnGroupFactory', 'JQXTreeNodeIdGenerator', 'ColumnIteratorService', 'CustomColumnCycleDetectionService'];
module.exports = JQXTreeFactory;